import AdminHolidayListTemplate from '@/components/templates/AdminHolidayListTemplate';
import { useAdminHolidayList } from '@/hooks/admin/useAdminHolidayList';
import { useAdminHolidayCSV } from '@/hooks/admin/useAdminHolidayCSV';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';
import { Holiday, HolidayFilterParams } from '@/types/admin-holiday';
import { japaneseDateToISO } from '@/utils/dateConverter';
import {
  buildHolidaySearchParams,
  getCurrentYearHolidayDefaults,
} from '@/utils/holidaySearchParams';
import { SorterResult } from 'antd/es/table/interface';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const HolidayListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setEnterKeyNavigationEnabled } = useGlobalBehavior();

  useEffect(() => {
    setEnterKeyNavigationEnabled(false);
    return () => setEnterKeyNavigationEnabled(true);
  }, [setEnterKeyNavigationEnabled]);

  const defaults = getCurrentYearHolidayDefaults();
  const rawSortBy = searchParams.get('sort_by')?.trim() || null;
  const sortBy = rawSortBy === 'holiday_name' ? null : rawSortBy;
  const order = searchParams.get('order') as 'ascend' | 'descend' | null;
  const holidayName = searchParams.get('holiday_name');
  const startDate = searchParams.get('start_date') || defaults.start_date;
  const endDate = searchParams.get('end_date') || defaults.end_date;

  const breadcrumbItems = [{ title: '休日' }];

  const { data, isLoading } = useAdminHolidayList({
    sort_by: sortBy || undefined,
    order: order || undefined,
    holiday_name: holidayName || undefined,
    start_date: japaneseDateToISO(startDate) || startDate,
    end_date: japaneseDateToISO(endDate) || endDate,
  });

  // BE returns both official 国民の祝日 and non-official observances (母の日,
  // クリスマス, ...); this screen only displays official ones, so filter by
  // is_official on the client and re-number `no` for a contiguous sequence.
  const listData = (data || [])
    .filter(h => h.is_official)
    .map((h, i) => ({ ...h, no: i + 1 }));

  const { handleCsvDownload, csvLoading } = useAdminHolidayCSV();
  const handleCsv = useCallback(() => {
    handleCsvDownload(listData);
  }, [handleCsvDownload, listData]);

  const handleSearch = (filters: HolidayFilterParams) => {
    setSearchParams(buildHolidaySearchParams(filters));
  };

  const handleTableChange = (sorter: SorterResult<Holiday> | SorterResult<Holiday>[]) => {
    const params = new URLSearchParams(searchParams);
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (singleSorter.order) {
      params.set('sort_by', (singleSorter.field || singleSorter.columnKey) as string);
      params.set('order', singleSorter.order);
    } else {
      params.delete('sort_by');
      params.delete('order');
    }

    setSearchParams(params);
  };

  return (
    <AdminHolidayListTemplate
      data={listData}
      sortInfo={{
        columnKey: sortBy || undefined,
        order: order || undefined,
      }}
      onTableChange={handleTableChange}
      onSearch={handleSearch}
      onCsv={handleCsv}
      csvLoading={csvLoading}
      breadcrumbItems={breadcrumbItems}
      isLoading={isLoading}
    />
  );
};

export default HolidayListPage;
